/**
 * RecordCheckPayment — Iter-161 · Phase 4.
 *
 * Finance-only inline action inside the Billing & A/R tab. Lists all
 * unpaid invoices with a "Record Check" button. Clicking opens a
 * modal with check_number + amount + received_at + notes. On submit,
 * POSTs to /iter156/payment-methods/check/record which flips the
 * invoice to PAID/GREEN + logs a Finance notification.
 */
import { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Loader2, FileText, CheckCircle2, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function RecordCheckPayment({ onRecorded }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ check_number: '', amount: '', received_at: '', notes: '' });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/iter156/invoices?status=`, { credentials: 'include' });
      if (r.ok) {
        const d = await r.json();
        setInvoices((d.invoices || []).filter((i) => !i.paid));
      }
    } catch (_) {}
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const open = (inv) => {
    setSelected(inv);
    setForm({ check_number: '', amount: String(inv.total || ''), received_at: new Date().toISOString().slice(0, 10), notes: '' });
    setMsg(''); setErr('');
  };
  const close = () => { setSelected(null); setBusy(false); };

  const submit = async () => {
    if (!selected) return;
    if (!form.check_number || !form.amount) { setErr('Check number and amount required.'); return; }
    setBusy(true); setMsg(''); setErr('');
    try {
      const r = await fetch(`${API}/api/iter156/payment-methods/check/record`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          property_id: selected.property_id,
          invoice_number: selected.invoice_number,
          check_number: form.check_number,
          amount: Number(form.amount),
          received_at: form.received_at ? new Date(form.received_at).toISOString() : undefined,
          notes: form.notes || undefined,
        }),
      });
      const d = await r.json();
      if (r.ok && d.status === 'posted') {
        setMsg(`Check ${form.check_number} posted — invoice ${d.invoice_number} marked PAID.`);
        await load();
        onRecorded && onRecorded();
        setTimeout(close, 900);
      } else {
        setErr(d.detail || 'Failed to record check.');
      }
    } catch (e) {
      setErr(String(e.message || e).slice(0, 200));
    }
    setBusy(false);
  };

  return (
    <Card className="p-4 bg-[#141414] border-zinc-800" data-testid="record-check-payment-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-emerald-400" />
          <p className="text-[10px] uppercase tracking-widest text-zinc-300">Record Check Payment</p>
        </div>
        <Button size="sm" onClick={load} className="text-[10px] uppercase" style={{ backgroundColor: '#27272a', color: '#fff' }} data-testid="record-check-refresh">
          Refresh
        </Button>
      </div>
      <div className="mt-3 rounded border border-zinc-800 overflow-x-auto">
        <table className="admin-stack-table text-xs" data-testid="record-check-table">
          <thead className="bg-black/40 text-zinc-400 uppercase text-[10px] tracking-widest">
            <tr>
              <th className="text-left p-2">Invoice</th>
              <th className="text-left p-2">Customer</th>
              <th className="text-right p-2">Amount</th>
              <th className="text-left p-2">Due</th>
              <th className="text-center p-2">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="5" className="p-4 text-center text-zinc-500">Loading…</td></tr>}
            {!loading && invoices.length === 0 && <tr><td colSpan="5" className="p-4 text-center text-zinc-500">No unpaid invoices.</td></tr>}
            {invoices.slice(0, 25).map((inv) => (
              <tr key={inv.invoice_number} className="border-t border-zinc-800" data-testid={`record-check-row-${inv.invoice_number}`}>
                <td className="p-2 font-bold" data-label="Invoice" style={{ color: '#C9A962' }}>{inv.invoice_number}</td>
                <td className="p-2 text-zinc-300" data-label="Customer">{inv.customer_legal_name}</td>
                <td className="p-2 text-right text-white" data-label="Amount">${Number(inv.total || 0).toLocaleString()}</td>
                <td className="p-2 text-zinc-400" data-label="Due">{(inv.due_date || '').slice(0, 10)}</td>
                <td className="p-2 text-center" data-label="Action">
                  <Button size="sm" onClick={() => open(inv)}
                          className="text-[10px] uppercase font-bold"
                          style={{ backgroundColor: '#10b981', color: '#fff' }}
                          data-testid={`record-check-open-${inv.invoice_number}`}>
                    Record Check
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="record-check-modal">
          <Card className="p-5 w-full max-w-lg bg-[#0f0f0f] border-zinc-700 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm font-bold text-white uppercase tracking-widest">Record Check — {selected.invoice_number}</p>
              <button onClick={close} className="text-zinc-500 hover:text-white" data-testid="record-check-close"><X className="h-4 w-4" /></button>
            </div>
            <p className="text-xs text-zinc-400">Customer: <span className="text-white">{selected.customer_legal_name}</span></p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-zinc-400">Check #</label>
                <input type="text" value={form.check_number}
                       onChange={(e) => setForm({ ...form, check_number: e.target.value })}
                       className="w-full rounded bg-zinc-900 border border-zinc-700 text-white text-xs p-2"
                       data-testid="record-check-input-number" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-zinc-400">Amount ($)</label>
                <input type="number" step="0.01" value={form.amount}
                       onChange={(e) => setForm({ ...form, amount: e.target.value })}
                       className="w-full rounded bg-zinc-900 border border-zinc-700 text-white text-xs p-2"
                       data-testid="record-check-input-amount" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-zinc-400">Received (date)</label>
                <input type="date" value={form.received_at}
                       onChange={(e) => setForm({ ...form, received_at: e.target.value })}
                       className="w-full rounded bg-zinc-900 border border-zinc-700 text-white text-xs p-2"
                       data-testid="record-check-input-date" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-zinc-400">Notes (optional)</label>
                <input type="text" value={form.notes}
                       onChange={(e) => setForm({ ...form, notes: e.target.value })}
                       className="w-full rounded bg-zinc-900 border border-zinc-700 text-white text-xs p-2"
                       data-testid="record-check-input-notes" />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 pt-1">
              <Button size="sm" onClick={close}
                      className="text-[10px] uppercase"
                      style={{ backgroundColor: '#3f3f46', color: '#fff' }}
                      data-testid="record-check-cancel">Cancel</Button>
              <Button size="sm" onClick={submit} disabled={busy}
                      className="text-[10px] uppercase font-bold"
                      style={{ backgroundColor: '#10b981', color: '#fff' }}
                      data-testid="record-check-submit">
                {busy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <CheckCircle2 className="h-3 w-3 mr-1" />}
                Post payment
              </Button>
            </div>
            {msg && <p className="text-xs" style={{ color: '#7CFC8A' }} data-testid="record-check-msg">{msg}</p>}
            {err && <p className="text-xs text-red-400" data-testid="record-check-err">{err}</p>}
          </Card>
        </div>
      )}
    </Card>
  );
}
